import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import ReactTooltip from 'react-tooltip';

import { getFullName } from 'modules/common/helpers';
import { Col, Row } from 'modules/bootstrap/components';
import {
  SelectController,
  InfoTooltip,
  TextAreaRef,
  Button,
} from 'modules/common/components';
import { BequestAssetCard, BequestAssetForm } from 'modules/asset/components';
import { WILL_GENERATOR_ANSWER_KEYS } from 'modules/assistant/constants/questionnaires/will';

import iconPlus from 'images/icon-plus.svg';

type Inputs = {
  id?: string;
  bequest?: string;
  from?: SelectOption;
  to: SelectOption;
  include: string;
};

interface BequestFormProps {
  bequestItem?: BequestDTO;
  onCancel: () => void;
  onSuccess: (newBequest: any) => void;
  isSpousalWill?: boolean;
}

function BequestForm(props: BequestFormProps) {
  const { onCancel, onSuccess, isSpousalWill } = props;
  const bequestItem = (props.bequestItem || {}) as BequestDTO;

  const { t } = useTranslation(['auth', 'common', 'contacts', 'will']);
  const {
    register,
    handleSubmit,
    control,

    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      include: bequestItem?.include || 'true',
    },
  });

  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>({});

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  const heirs: ContactDTO[] =
    useSelector((state: any) => state.heirs.list) || [];
  const heirsOptions = heirs.map((item) => ({
    value: item.id,
    label: getFullName(item, false),
  }));
  const defaultHeirsOptions = heirsOptions.find(
    (item) => item.value === bequestItem.to
  );

  const isNewContact = !bequestItem.id;

  const onSubmit = async (formData: Inputs) => {
    const from = isSpousalWill
      ? (formData?.from?.value as string) || ''
      : WILL_GENERATOR_ANSWER_KEYS.INDIVIDUAL;
    const submitData = {
      ...formData,
      bequest: !isEmpty(selectedAsset)
        ? selectedAsset.userAsset.label
        : formData?.bequest,
      from,
      to: (formData?.to?.value as string) || '',
    } as any;

    if (!isEmpty(selectedAsset)) {
      submitData.assetCategory = selectedAsset.assetCategory.value;
    }

    onSuccess(submitData);
    onCancel();
  };

  const handleAssetFormSuccess = (userAsset): void => {
    setSelectedAsset(userAsset);
  };

  const contactsOptions = [
    { value: 'partner', label: t('common:partner') },
    { value: 'myself', label: t('common:myself') },
  ];
  const defaultContactsOptions = contactsOptions.find(
    (item) => item.value === bequestItem.from
  );

  const textFieldRequired = t('common:field_required');

  return (
    <>
      {showEditForm && (
        <BequestAssetForm
          onCancel={() => setShowEditForm(false)}
          onSuccess={handleAssetFormSuccess}
        />
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`Form ${showEditForm ? 't-hidden' : ''}`}
      >
        {!isNewContact && (
          <input
            type="hidden"
            id="id"
            {...register('id')}
            value={bequestItem.id}
          />
        )}

        <h1 className="Typography Typography--title u-pb-30 u-weight-600">
          {t('will:bequest_form_title')}
        </h1>

        {!isEmpty(selectedAsset) ? (
          <Row className="t-pb-5">
            <Col>
              <BequestAssetCard
                assetCategory={selectedAsset.assetCategory.label}
                assetTitle={selectedAsset.userAsset.label}
              />
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col>
                <Button
                  category="secondary"
                  onClick={() => {
                    setShowEditForm(true);
                  }}
                >
                  <img
                    src={iconPlus}
                    className="button-icon-amazon"
                    alt={t('common:select_from_legacy_plan')}
                    role="presentation"
                  />
                  {t('common:select_from_legacy_plan')}
                </Button>
                <p className="u-pt-20">{t('common:or_create_new')}</p>
              </Col>
            </Row>

            <Row className="u-pt-20">
              <Col xl={{ span: 12 }}>
                <TextAreaRef
                  {...register('bequest', { required: true })}
                  label={t('common:description_label')}
                  defaultValue={bequestItem.bequest}
                  maxLength={2000}
                  rows={3}
                  error={errors.bequest && textFieldRequired}
                />
              </Col>
            </Row>
          </>
        )}

        {isSpousalWill && (
          <Row>
            <Col>
              <div className="Form-group">
                <label
                  className={`Form-label ${errors.from ? 'isErrored' : ''}`}
                  htmlFor="subtype"
                >
                  {t('will:bequest_from_label')}
                </label>

                <SelectController
                  id="from"
                  name="from"
                  options={contactsOptions}
                  control={control}
                  defaultValue={defaultContactsOptions}
                  className={`Select ${errors.from ? 'isErrored' : ''}`}
                  rules={{ required: true }}
                />
                {errors.from && (
                  <div className="Form-alert">
                    <span className="text-main-sm t-text-epsilon-600">
                      {textFieldRequired}
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <div className="Form-group">
              <label
                className={`Form-label ${errors.to ? 'isErrored' : ''}`}
                htmlFor="subtype"
              >
                {t('will:bequest_to_label')}
              </label>

              <SelectController
                id="to"
                name="to"
                options={heirsOptions as SelectOption[]}
                control={control}
                defaultValue={defaultHeirsOptions}
                className={`Select ${errors.to ? 'isErrored' : ''}`}
                rules={{ required: true }}
              />
              {errors.to && (
                <div className="Form-alert">
                  <span className="text-main-sm t-text-epsilon-600">
                    {textFieldRequired}
                  </span>
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Row className="t-pb-5 u-pt-20">
          <Col xl={{ span: 12 }}>
            <label
              className="Radiobutton"
              tabIndex={0}
              role="radio"
              aria-checked="false"
              aria-labelledby="radioLabel"
              htmlFor="include"
            >
              <input
                id="include"
                value="true"
                {...register('include')}
                type="radio"
                className="jsCeckboxSource Radiobutton-source"
              />
              <span className="Radiobutton-result" />
              <span className="Radiobutton-caption t-mr-2.5" id="radioLabel">
                {t('will:include_bequest_label')}
              </span>
              <InfoTooltip tipText={t('will:include_bequest_tooltip')} />
            </label>
          </Col>
        </Row>

        <Row className="t-pb-5">
          <Col xl={{ span: 12 }}>
            <label
              className="Radiobutton"
              tabIndex={0}
              role="radio"
              htmlFor="exclude"
              aria-checked="false"
              aria-labelledby="radioLabel2"
            >
              <input
                id="exclude"
                value="false"
                {...register('include')}
                type="radio"
                className="jsCeckboxSource Radiobutton-source"
              />
              <span className="Radiobutton-result" />
              <span className="Radiobutton-caption t-mr-2.5" id="radioLabel2">
                {t('will:exclude_bequest_label')}
              </span>
              <InfoTooltip tipText={t('will:exclude_bequest_tooltip')} />
            </label>
          </Col>
        </Row>

        <div className="t-flex t-flex-wrap t-justify-end">
          <Button
            category="secondary"
            className="t-whitespace-nowrap t-self-end"
            onClick={onCancel}
          >
            {t('common:cancel')}
          </Button>
          <Button type="submit" className="t-ml-5 xs:t-mt-5">
            {t('common:save_and_continue')}
          </Button>
        </div>
      </form>
    </>
  );
}

export default BequestForm;
